import { DownloadOutlined, FileExcelOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  DatePicker,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useReportsCreateReport,
  useReportsListReports,
  useReportsRebuild,
} from '@/api/generated/endpoints/reports/reports';
import type { ReportKind, ReportOut, ReportStatus } from '@/api/generated/model';
import { problemMessage } from '@/i18n/problem';
import { ShareFileModal } from '@/components/ShareFileModal';
import { downloadFile, fetchFile } from '@/lib/download';
import { shareFirst } from '@/lib/share';
import { lastMonth, lastWeek, thisMonth, thisWeek, yearToDate, type Range } from '@/lib/periods';
import { invalidatePath } from '@/lib/queryCache';
import { useIsMobile } from '@/lib/useIsMobile';
import { useTableState } from '@/lib/useTableState';
import { isDesktop } from '@/runtime';

const DATE = 'DD.MM.YYYY';
const DATETIME = 'DD.MM.YYYY HH:mm';
const API_DATE = 'YYYY-MM-DD';
const POLL_MS = 2_000;
const IN_PROGRESS: ReportStatus[] = ['queued', 'running'];

const STATUS_COLOR: Record<ReportStatus, string> = {
  queued: 'default',
  running: 'processing',
  done: 'green',
  failed: 'red',
};

type Tab = 'all' | ReportKind;

const periodText = (start: string, end: string) =>
  `${dayjs(start).format(DATE)} – ${dayjs(end).format(DATE)}`;

/** Отчёты: заказ за период и архив (ручные и еженедельные). */
export function ReportsPage() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const table = useTableState<'tab'>({ filters: ['tab'] });
  const tab = (table.filters.tab[0] ?? 'all') as Tab;
  const mobile = useIsMobile();
  const shareMode = shareFirst(mobile);
  const [fetching, setFetching] = useState<number | null>(null);
  const [shared, setShared] = useState<File | null>(null);
  const [range, setRange] = useState<Range>(() => lastWeek(dayjs()));

  const list = useReportsListReports(
    { ...table.query, kind: tab === 'all' ? null : tab },
    {
      query: {
        // Пока отчёт собирается — опрашиваем: «Готов» и кнопка скачивания появятся сами.
        refetchInterval: (q) =>
          q.state.data?.items.some((r) => IN_PROGRESS.includes(r.status)) ? POLL_MS : false,
      },
    },
  );
  const create = useReportsCreateReport();
  const rebuild = useReportsRebuild();

  async function rebuildReport(r: ReportOut) {
    try {
      await rebuild.mutateAsync({ reportId: r.id });
      void message.success(
        t('reports.queued', { period: periodText(r.period_start, r.period_end) }),
      );
      await invalidatePath(queryClient, '/api/v1/reports');
    } catch (e) {
      void message.error(problemMessage(e));
    }
  }

  async function generate() {
    const [start, end] = range.map((d) => d.format(API_DATE)) as [string, string];
    try {
      await create.mutateAsync({ data: { start, end } });
      void message.success(t('reports.queued', { period: periodText(start, end) }));
      if (tab === 'weekly') table.setFilter('tab', null);
      await invalidatePath(queryClient, '/api/v1/reports');
    } catch (e) {
      void message.error(problemMessage(e));
    }
  }

  const today = dayjs();
  const presets = [
    { label: t('reports.lastWeek'), value: lastWeek(today) },
    { label: t('reports.thisWeek'), value: thisWeek(today) },
    { label: t('reports.lastMonth'), value: lastMonth(today) },
    { label: t('reports.thisMonth'), value: thisMonth(today) },
    { label: t('reports.yearToDate'), value: yearToDate(today) },
  ];

  const statusCell = (r: ReportOut) => (
    <Space size={4} wrap>
      <Tooltip title={r.status === 'failed' ? r.error : undefined}>
        <Tag color={STATUS_COLOR[r.status]}>{t(`reports.status.${r.status}`)}</Tag>
      </Tooltip>
      {r.corrections_after > 0 && (
        // ADR-0012 п. 7: файл не меняется — пометка и пересборка новым отчётом.
        <Tooltip title={t('reports.correctedHint', { count: r.corrections_after })}>
          <Tag color="gold">{t('reports.corrected')}</Tag>
        </Tooltip>
      )}
    </Space>
  );

  const fileCell = (r: ReportOut) =>
    r.status === 'done' ? (
      <Space direction="vertical" size={4}>
        <Button
          icon={<DownloadOutlined />}
          loading={fetching === r.id}
          {...(shareMode
            ? {
                // Телефон (T-174): не уходить на страницу файла (из приложения с экрана
                // «Домой» с неё нет пути назад) — загрузить и предложить «Поделиться».
                onClick: () => {
                  setFetching(r.id);
                  fetchFile(`/api/v1/reports/${r.id}/file`, `report-${r.id}.xlsx`)
                    .then(setShared, (e: unknown) => {
                      void message.error(problemMessage(e));
                    })
                    .finally(() => {
                      setFetching(null);
                    });
                },
              }
            : isDesktop
              ? {
                  onClick: () => {
                    downloadFile(`/api/v1/reports/${r.id}/file`, `report-${r.id}.xlsx`).catch(
                      (e: unknown) => {
                        void message.error(problemMessage(e));
                      },
                    );
                  },
                }
              : { href: `/api/v1/reports/${r.id}/file` })}
          title={t('reports.sizeKb', {
            n: Math.max(1, Math.round((r.size_bytes ?? 0) / 1024)),
          })}
        >
          {t('reports.download')}
        </Button>
        {r.corrections_after > 0 &&
          (r.rebuilt_by === null ? (
            <Button
              icon={<ReloadOutlined />}
              loading={rebuild.isPending && rebuild.variables.reportId === r.id}
              onClick={() => void rebuildReport(r)}
            >
              {t('reports.rebuild')}
            </Button>
          ) : (
            <Typography.Text type="secondary">
              {t('reports.rebuiltAs', { id: r.rebuilt_by })}
            </Typography.Text>
          ))}
      </Space>
    ) : null;

  // Телефон: период, вид, статус и дата заказа — в одной ячейке; узкие колонки ломали даты по символу.
  const mobileColumns: TableColumnsType<ReportOut> = [
    {
      key: 'report',
      title: t('reports.colPeriod'),
      render: (_, r) => (
        <Space direction="vertical" size={4}>
          <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>
            {periodText(r.period_start, r.period_end)}
          </Typography.Text>
          <Space size={4} wrap>
            <Tag color={r.kind === 'weekly' ? 'purple' : 'blue'}>{t(`reports.kind.${r.kind}`)}</Tag>
            {statusCell(r)}
          </Space>
          <Typography.Text type="secondary">
            {dayjs(r.created_at).format(DATETIME)} · {r.locale.toUpperCase()}
          </Typography.Text>
          {fileCell(r)}
        </Space>
      ),
    },
  ];

  const columns: TableColumnsType<ReportOut> = [
    {
      key: 'period',
      title: t('reports.colPeriod'),
      render: (_, r) => (
        <span style={{ whiteSpace: 'nowrap' }}>{periodText(r.period_start, r.period_end)}</span>
      ),
    },
    {
      key: 'kind',
      title: t('reports.colKind'),
      dataIndex: 'kind',
      width: 140,
      render: (k: ReportKind) => (
        <Tag color={k === 'weekly' ? 'purple' : 'blue'}>{t(`reports.kind.${k}`)}</Tag>
      ),
    },
    {
      key: 'locale',
      title: t('reports.colLocale'),
      dataIndex: 'locale',
      width: 70,
      render: (l: string) => l.toUpperCase(),
    },
    {
      key: 'author',
      title: t('reports.colAuthor'),
      dataIndex: 'requested_by_name',
      ellipsis: true,
      render: (name: string | null) =>
        name ?? <Typography.Text type="secondary">{t('reports.auto')}</Typography.Text>,
    },
    {
      key: 'created_at',
      title: t('reports.colCreated'),
      dataIndex: 'created_at',
      width: 140,
      render: (d: string) => dayjs(d).format(DATETIME),
    },
    {
      key: 'status',
      title: t('reports.colStatus'),
      dataIndex: 'status',
      width: 130,
      render: (_: ReportStatus, r) => statusCell(r),
    },
    {
      key: 'rows',
      title: t('reports.colRows'),
      width: 170,
      render: (_, r) =>
        r.status === 'done'
          ? t('reports.rows', { receipts: r.receipts_count, lines: r.shipment_lines_count })
          : null,
    },
    {
      key: 'file',
      width: 160,
      render: (_, r) => fileCell(r),
    },
  ];

  return (
    <Card title={t('reports.title')}>
      <Space wrap align="center" style={{ marginBottom: 8 }}>
        <DatePicker.RangePicker
          aria-label={t('reports.period')}
          format={DATE}
          allowClear={false}
          value={range}
          presets={presets}
          disabledDate={(d) => d.isAfter(dayjs(), 'day')}
          onChange={(v) => {
            if (v?.[0] && v[1]) setRange([v[0], v[1]]);
          }}
        />
        <Button
          type="primary"
          icon={<FileExcelOutlined />}
          loading={create.isPending}
          onClick={() => void generate()}
        >
          {t('reports.generate')}
        </Button>
      </Space>
      <Typography.Paragraph type="secondary">{t('reports.hint')}</Typography.Paragraph>
      <Tabs
        activeKey={tab}
        onChange={(k) => {
          table.setFilter('tab', k === 'all' ? null : k);
        }}
        items={[
          { key: 'all', label: t('reports.tabAll') },
          { key: 'manual', label: t('reports.tabManual') },
          { key: 'weekly', label: t('reports.tabWeekly') },
        ]}
      />
      {shared && (
        <ShareFileModal
          title={t('reports.title')}
          file={shared}
          sendText={t('reports.send')}
          hint={t('reports.sendHint')}
          onClose={() => {
            setShared(null);
          }}
        />
      )}
      <Table<ReportOut>
        // Колонки с «…» включают фиксированную раскладку: в узком окне (программа для Windows,
        // 900–1300 px) — прокрутка внутри таблицы, а не колонки шириной в букву; период не наезжает на «Тип».
        scroll={mobile ? {} : { x: 1200 }}
        rowKey="id"
        columns={mobile ? mobileColumns : columns}
        dataSource={list.data?.items}
        loading={list.isPending}
        pagination={table.pagination(list.data?.total)}
        onChange={table.onTableChange}
        locale={{ emptyText: t('reports.empty') }}
      />
    </Card>
  );
}
